import { Tooltip } from '@mantine/core';
import { BroomIcon } from 'common/assets';
import {
  FormAutoSaver,
  FormAutoSaverType,
} from 'common/helpers/form-auto-saver';
import { themedStyled } from 'common/styles/styletron.theme';
import invariant from 'invariant';
import Head from 'next/head';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import {
  FormProvider,
  FormProviderProps,
  SubmitHandler,
} from 'react-hook-form';
import { StyleObject } from 'styletron-standard';

import { Input } from '.';
import { Button } from './buttons';

export interface FormStateProps {
  editable: boolean;
  setIsEditable: React.Dispatch<React.SetStateAction<boolean>>;
}

enum RequestMethod {
  GET = 'GET',
  HEAD = 'HEAD',
  POST = 'POST',
  PUT = 'PUT',
  PATCH = 'PATCH',
  DELETE = 'DELETE',
  OPTIONS = 'OPTIONS',
}

export interface FormProps {
  methods: Omit<FormProviderProps<any>, 'children'>;
  children: React.ReactNode;
  method?: RequestMethod;
  action?: string;
  style?: React.CSSProperties;
  defaultEditable?: boolean;
  onSubmit: SubmitHandler<any>;
  customViewName?: string;
  keyName?: string;
  noReset?: boolean;
}

export const FormContext = React.createContext<FormStateProps>({
  editable: false,
  setIsEditable: () => {},
});

interface FormFooterProps {
  showCancel?: boolean;
  showDelete?: boolean;
  showSubmit?: boolean;
  onDelete?: () => void;
  onCancel?: () => void;
  isLoading?: boolean;
  $style?: StyleObject | any;
  renderRight?: React.ReactNode;
}

export function FormFooter(props: FormFooterProps) {
  const { t } = useTranslation();
  const {
    showCancel = false,
    showDelete = false,
    showSubmit = true,
    onDelete,
    isLoading,
    $style,
    renderRight,
    onCancel,
  } = props;
  return (
    <ButtonContainer $style={$style}>
      <ButtonLeftContent>
        {showDelete && (
          <>
            <Button
              buttonType="secondary"
              type="button"
              onClick={onDelete}
              isLoading={isLoading}
              isError
            >
              {t('common:delete')}
            </Button>
            <div
              style={{
                marginLeft: 24,
              }}
            />
          </>
        )}
      </ButtonLeftContent>
      <ButtonRightContent>
        {showCancel && (
          <>
            <Button buttonType="secondary" onClick={onCancel}>
              {t('common:cancel')}
            </Button>
            <div
              style={{
                marginLeft: 16,
              }}
            />
          </>
        )}
        {showSubmit && <Input type="submit" isLoading={isLoading} />}
        {renderRight}
      </ButtonRightContent>
    </ButtonContainer>
  );
}

export type FormRef = {
  editable: boolean;
  setIsEditable: React.Dispatch<React.SetStateAction<boolean>>;
};

const Form = React.forwardRef<FormRef, FormProps>((props, ref) => {
  const { t } = useTranslation();
  const [isEditable, setIsEditable] = React.useState(
    props.defaultEditable !== undefined ? props.defaultEditable : true,
  );
  const {
    methods,
    style,
    children,
    action,
    method = 'POST',
    onSubmit,
    customViewName,
    keyName,
    noReset,
  } = props;

  const {
    getValues,
    formState: { isSubmitted },
  } = methods;

  React.useImperativeHandle(ref, () => {
    return {
      editable: isEditable,
      setIsEditable,
    };
  });

  React.useEffect(() => {
    if (!isEditable) return;
    if (!keyName) return;

    const saveData = setInterval(() => {
      FormAutoSaver.set(keyName!, getValues());
    }, 3000);

    if (isSubmitted) {
      clearInterval(saveData);
      FormAutoSaver.remove(keyName);
    }

    return () => {
      clearInterval(saveData);
    };
  }, [isEditable, keyName, isSubmitted, getValues]);

  React.useEffect(() => {
    if (!isEditable) return;
    if (!keyName) return;

    const savedData = FormAutoSaver.get(keyName) as FormAutoSaverType;

    if (savedData && savedData.data) {
      Object.keys(savedData.data).forEach((key) => {
        methods.setValue(key, savedData.data[key], { shouldValidate: true });
      });
    }
  }, [isEditable, methods, keyName]);

  React.useEffect(() => {
    if (!keyName) return;
    if (!isEditable) {
      FormAutoSaver.remove(keyName);
    }
  }, [isEditable, keyName]);

  const value = React.useMemo<FormStateProps>(
    () => ({
      editable: isEditable && !methods.formState.isSubmitting,
      setIsEditable,
    }),
    [isEditable, methods.formState.isSubmitting],
  );

  return (
    <>
      {customViewName && (
        <Head>
          <title>{customViewName}</title>
        </Head>
      )}
      <FormContext.Provider value={value}>
        <FormProvider {...methods}>
          <form
            style={{
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
              ...style,
            }}
            method={method}
            action={action}
            onSubmit={(e) => {
              e.stopPropagation();
              if (!action) {
                e.preventDefault();
                methods.handleSubmit(onSubmit)();
              }
              if (keyName) {
                FormAutoSaver.remove(keyName);
              }
            }}
          >
            {children}
          </form>
        </FormProvider>
      </FormContext.Provider>
      {keyName && isEditable && !noReset && (
        <Tooltip label={t('common:reset_form')} position="left" withArrow>
          <ResetButtonContainer>
            <Button
              style={{
                paddingRight: '8px',
                paddingLeft: '8px',
                borderRadius: '50% !important',
                boxShadow: '0px 0px 8px gray',
              }}
              onClick={() => {
                FormAutoSaver.remove(keyName);
                methods.reset();
              }}
            >
              <BroomIcon size={16} />
            </Button>
          </ResetButtonContainer>
        </Tooltip>
      )}
    </>
  );
});

Form.displayName = 'Form';

export default Form;

interface Props {
  children: (context: FormStateProps) => any;
}

export function FormState(props: Props) {
  const context = React.useContext(FormContext);
  return props.children(context);
}

export function useFormState(): FormStateProps {
  const context = React.useContext(FormContext);
  invariant(
    context !== undefined,
    'useFormState must be used inside Form Container',
  );
  return context;
}

const ButtonContainer = themedStyled('div', {
  padding: '16px',
  display: 'flex',
  width: '100%',
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: 'white',
  justifyContent: 'space-between',
  boxShadow: '0px 0 2px rgba(0, 0, 0, 0.4)',
  zIndex: 90,
});

const ButtonRightContent = themedStyled('div', {
  display: 'flex',
});

const ButtonLeftContent = themedStyled('div', {
  display: 'flex',
});

export const ResetButtonContainer = themedStyled('div', ({ $theme }) => ({
  position: 'absolute',
  right: '24px',
  bottom: '150px',
  zIndex: 4,
  [$theme.mediaQuery.md]: {
    bottom: '90px',
  },
}));
